import { inject } from "@vercel/analytics";
import React, { PropsWithChildren, useEffect, useState } from "react";

import { getOutgoingLinkTarget } from "modules/account/account.outgoing-links";
import {
  SettingsProvider,
  SettingsProviderProps,
} from "shared/SettingsProvider";
import { withApp } from "utils/with-app.utils";

import { Footer } from "./Footer";
import { Navbar } from "./Navbar";
import { StrippedNavbar } from "./Navbar/StrippedNavbar";

type LayoutProps = {
  withBanner?: boolean;
  // Opt-in (login page): from the switch date, logo only, no menu, banner or footer.
  stripAfterSwitch?: boolean;
  // Set at build time: true when the page was built after the switch.
  isChromeRemovedAtBuild?: boolean;
  settings?: Partial<SettingsProviderProps>;
};

// The page is static, so its HTML may be older than the switch date.
// The server and the first client render read the build-time prop only.
// After mount, the browser also reads the clock.
const useIsStripped = (
  stripAfterSwitch?: boolean,
  isChromeRemovedAtBuild?: boolean,
) => {
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    if (stripAfterSwitch) {
      setIsMounted(true);
    }
  }, [stripAfterSwitch]);

  if (!stripAfterSwitch) {
    return false;
  }

  return (
    !!isChromeRemovedAtBuild ||
    (isMounted && getOutgoingLinkTarget("loginPageChrome") === null)
  );
};

export const Layout = ({
  children,
  withBanner,
  stripAfterSwitch,
  isChromeRemovedAtBuild,
  settings,
}: PropsWithChildren<LayoutProps>) => {
  inject();
  const isStripped = useIsStripped(stripAfterSwitch, isChromeRemovedAtBuild);

  return (
    <SettingsProvider isBlackFriday={!!settings?.isBlackFriday}>
      {isStripped ? <StrippedNavbar /> : <Navbar withBanner={withBanner} />}

      {children}

      {!isStripped && <Footer />}
    </SettingsProvider>
  );
};

export const AppLayout = withApp(Layout, [], true);
